import { faStar, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { Routes } from '../../classes/Routes';
import Alert from '../../components/UI/Alert/Alert';
import Container from '../../components/UI/Container/Container';
import Spinner from '../../components/UI/Spinner/Spinner';
import Table from '../../components/UI/Table/Table';
import TableTh from '../../components/UI/Table/TableTh/TableTh';
import { useTableSort } from '../../components/UI/Table/useTableSort';
import Button, { EButtonColor } from '../../components/UI/Button/Button';
import ConfirmDialog from '../../components/UI/ConfirmDialog/ConfirmDialog';
import ModalContext, { defaultModal, EModalSize } from '../../components/UI/Modal/ModalContext';
import { IAppState } from '../../interfaces';
import { dateToDateTimeString } from '../../shared/utility';
import * as actions from '../../store/actions';
import { ETranslation } from '../../translations/translation-keys';
import classes from './Feedbacks.module.scss';

const Feedbacks: React.FC<RouteComponentProps> = () => {
  const { loading, error, feedbacks } = useSelector((state: IAppState) => ({
    loading: state.feedbacks.loading,
    error: state.feedbacks.error,
    feedbacks: state.feedbacks.feedbacks,
  }));

  const feedbacksWithComputed = useMemo(() => {
    if (!feedbacks) return null;
    return feedbacks.map(f => {
      const orName = (f.offerRequest?.firstName || f.offerRequest?.lastName)
        ? `${f.offerRequest?.firstName || ''} ${f.offerRequest?.lastName || ''}`.trim()
        : '';
      const customerName = orName || f.name || '';
      return {
        ...f,
        customerName,
      };
    });
  }, [feedbacks]);

  const { sort, onSort, items: sortedFeedbacks } = useTableSort({ 
    data: feedbacksWithComputed,
    defaultSort: { key: 'created', reverse: true, type: 'date' }
  });
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const { setModal } = useContext(ModalContext);

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const isSyncingScroll = useRef(false);

  const [scrollWidth, setScrollWidth] = useState<number>(0);
  const [tableRect, setTableRect] = useState<{ left: number; width: number } | null>(null);
  const [showFloatingBottom, setShowFloatingBottom] = useState<boolean>(false);

  const syncScroll = (source: HTMLDivElement | null, target: HTMLDivElement | null) => {
    if (!source || !target || isSyncingScroll.current) return;
    isSyncingScroll.current = true;
    target.scrollLeft = source.scrollLeft;
    requestAnimationFrame(() => {
      isSyncingScroll.current = false;
    });
  };

  const handleTableScroll = () => {
    syncScroll(tableWrapperRef.current, bottomScrollRef.current);
  };

  const handleBottomScroll = () => {
    syncScroll(bottomScrollRef.current, tableWrapperRef.current);
  };

  const updateScrollState = useCallback(() => {
    const el = tableWrapperRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const tableEl = el.querySelector('table');
    const fullWidth = tableEl ? tableEl.scrollWidth : el.scrollWidth;
    setScrollWidth(fullWidth);
    setTableRect({ left: rect.left, width: rect.width });

    const hasHorizontalOverflow = fullWidth > el.clientWidth + 5;
    const isTableInView = rect.top < window.innerHeight && rect.bottom > 80;
    const isBottomOffscreen = rect.bottom > window.innerHeight;

    setShowFloatingBottom(hasHorizontalOverflow && isTableInView && isBottomOffscreen);
  }, []);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(actions.listFeedbacks());
  }, [dispatch]);

  useEffect(() => {
    updateScrollState();
    window.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && tableWrapperRef.current) {
      observer = new ResizeObserver(updateScrollState);
      observer.observe(tableWrapperRef.current);
    }

    return () => {
      window.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      if (observer) observer.disconnect();
    };
  }, [updateScrollState, feedbacks]);

  useEffect(() => {
    if (showFloatingBottom && bottomScrollRef.current && tableWrapperRef.current) {
      bottomScrollRef.current.scrollLeft = tableWrapperRef.current.scrollLeft;
    }
  }, [showFloatingBottom]);

  const mapStars = (num?: number) => {
    if (!num) return '-';
    return (
      <span title={`${num}/5`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
        <span style={{ fontWeight: 600, minWidth: '10px' }}>{num}</span>
        <span>
          {[...Array(num)].map((_, i) => (
            <FontAwesomeIcon key={i} className={classes.Star} icon={faStar} />
          ))}
        </span>
      </span>
    );
  };

  const availableTenants = useMemo(() => {
    if (!feedbacks) return [];
    const set = new Set<string>();
    feedbacks.forEach(f => {
      const name = (f.source === 'google_forms' || f.source === 'public')
        ? f.tenantId
        : f.offerRequest?.service?.name;
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [feedbacks]);

  const displayedFeedbacks = sortedFeedbacks?.filter(f => {
    if (sourceFilter !== 'all') {
      const isPublic = f.source === 'public' || (!f.offerRequest && f.source !== 'google_forms');
      const isCustomer = f.source === 'customer' || (f.offerRequest && !f.source);
      const isServiceProvider = f.source === 'serviceProvider';

      if (sourceFilter === 'public' && !isPublic) return false;
      if (sourceFilter === 'customer' && !isCustomer) return false;
      if (sourceFilter === 'serviceProvider' && !isServiceProvider) return false;
      if (sourceFilter === 'google_forms' && f.source !== 'google_forms') return false;
    }

    if (tenantFilter !== 'all') {
      const name = (f.source === 'google_forms' || f.source === 'public')
        ? f.tenantId
        : f.offerRequest?.service?.name;
      if (name !== tenantFilter) return false;
    }

    return true;
  });

  const showRequestInfo = (e: React.MouseEvent, feedback: any) => {
    e.stopPropagation();
    setModal({
      isOpen: true,
      title: 'Tekniset tiedot (RequestInfo)',
      size: EModalSize.MEDIUM,
      content: (
        <div style={{ padding: '1rem' }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {Object.entries(feedback.requestInfo.properties).map(([key, val]: any) => {
              const displayVal = val.stringValue || (val.nullValue === null ? 'null' : (val.booleanValue !== undefined ? val.booleanValue.toString() : JSON.stringify(val)));
              return (
                <li key={key} style={{ marginBottom: '0.75rem', wordBreak: 'break-word', fontSize: '0.9rem' }}>
                  <strong style={{ display: 'block', color: '#555' }}>{key}:</strong>
                  <span style={{ color: displayVal === 'null' ? '#aaa' : '#000' }}>{displayVal}</span>
                </li>
              );
            })}
          </ul>
          <div style={{ marginTop: '2rem', textAlign: 'right' }}>
            <Button onClick={() => setModal(defaultModal)}>{t(ETranslation.COMMON_CLOSE) || 'Sulje'}</Button>
          </div>
        </div>
      ),
    });
  };

  const confirmDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setModal({
      isOpen: true,
      title: t(ETranslation.COMMON_REMOVE),
      size: EModalSize.SMALL,
      content: (
        <ConfirmDialog
          body={<p>{t(ETranslation.CONFIRM_DELETE_FEEDBACK)}</p>}
          onConfirm={() => {
            dispatch(actions.deleteFeedback(id));
            setModal(defaultModal);
          }}
          onCancel={() => setModal(defaultModal)}
        />
      ),
    });
  };

  return (
    <>
      {error && <Alert children={error} />}
      <Container>
        <h2>{t(ETranslation.TITLE_FEEDBACKS)}</h2>
        {loading
          ? <Spinner />
          : feedbacks && sortedFeedbacks
            ? (
              <>
                <div className={classes.FiltersContainer}>
                  <div className={classes.FilterItem}>
                    <label htmlFor="sourceFilter">{t(ETranslation.FEEDBACK_SOURCE)}:</label>
                    <select 
                      id="sourceFilter" 
                      value={sourceFilter} 
                      onChange={(e) => setSourceFilter(e.target.value)}
                    >
                      <option value="all">{t(ETranslation.FEEDBACK_SOURCE_ALL)}</option>
                      <option value="public">{t(ETranslation.FEEDBACK_SOURCE_PUBLIC)}</option>
                      <option value="customer">{t(ETranslation.FEEDBACK_SOURCE_CUSTOMER)}</option>
                      <option value="serviceProvider">{t(ETranslation.FEEDBACK_SOURCE_SERVICEPROVIDER)}</option>
                      <option value="google_forms">{t(ETranslation.FEEDBACK_SOURCE_GOOGLE_FORMS)}</option>
                    </select>
                  </div>
                  {availableTenants.length > 0 && (
                    <div className={classes.FilterItem}>
                      <label htmlFor="tenantFilter">{t(ETranslation.COMMON_SERVICE)}:</label>
                      <select 
                        id="tenantFilter" 
                        value={tenantFilter} 
                        onChange={(e) => setTenantFilter(e.target.value)}
                      >
                        <option value="all">Kaikki palvelut</option>
                        {availableTenants.map(tName => (
                          <option key={tName} value={tName}>{tName}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div 
                  ref={tableWrapperRef}
                  className={classes.TableWrapper}
                  onScroll={handleTableScroll}
                >
                  <Table 
                    hover 
                    sortContext={{ sort, onSort }}
                    containerStyles={{ overflowX: 'unset' }}
                  >
                    <thead>
                      <tr>
                        <TableTh sort="source">{t(ETranslation.FEEDBACK_SOURCE)}</TableTh>
                        <TableTh sort="created" type="date">{t(ETranslation.COMMON_CREATED)}</TableTh>
                        <TableTh sort="customerName">{t(ETranslation.COMMON_NAME)}</TableTh>
                        <TableTh sort="offerRequest.id">{t(ETranslation.CUSTOMER_OFFER_REQUEST)}</TableTh>
                        <TableTh sort="offerRequest.service.name">{t(ETranslation.COMMON_SERVICE)}</TableTh>
                        <TableTh sort="serviceSatisfaction" type="int">{t(ETranslation.FEEDBACK_SATISFACTION)}</TableTh>
                        <TableTh sort="serviceProvider.name">{t(ETranslation.FEEDBACK_SERVICEPROVIDER)}</TableTh>
                        <TableTh sort="realizedPrice">{t(ETranslation.FEEDBACK_REALIZED_PRICE)}</TableTh>
                        <TableTh sort="offerCountSatisfaction">{t(ETranslation.FEEDBACK_OFFER_COUNT_SATISFACTION)}</TableTh>
                        <TableTh sort="trustScore" type="int">{t(ETranslation.FEEDBACK_TRUST_SCORE)}</TableTh>
                        <TableTh sort="message">{t(ETranslation.FEEDBACK_MESSAGE)}</TableTh>
                        <TableTh sort="">Info</TableTh>
                        <TableTh sort="">{t(ETranslation.COMMON_REMOVE)}</TableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedFeedbacks?.map(feedback => {
                        const isGoogleForms = feedback.source === 'google_forms';
                        const isPublic = feedback.source === 'public' || (!feedback.offerRequest && !isGoogleForms);
                        const isCustomer = feedback.source === 'customer' || (feedback.offerRequest && !feedback.source);
                        const isServiceProvider = feedback.source === 'serviceProvider';
                        
                        let sourceLabel = '-';
                        if (isGoogleForms) sourceLabel = t(ETranslation.FEEDBACK_SOURCE_GOOGLE_FORMS);
                        else if (isPublic) sourceLabel = t(ETranslation.FEEDBACK_SOURCE_PUBLIC);
                        else if (isCustomer) sourceLabel = t(ETranslation.FEEDBACK_SOURCE_CUSTOMER);
                        else if (isServiceProvider) sourceLabel = t(ETranslation.FEEDBACK_SOURCE_SERVICEPROVIDER);

                        const serviceName = (isPublic || isGoogleForms) ? (feedback.tenantId || '') : (feedback.offerRequest?.service?.name || '');
                        const emailAddress = feedback.offerRequest?.email || feedback.email || '';
                        
                        const emailLink = emailAddress ? (
                          <a href={`mailto:${emailAddress}?subject=${encodeURIComponent(`HankiX ${serviceName} palautteesi`)}`} onClick={(e) => e.stopPropagation()}>
                            ({emailAddress})
                          </a>
                        ) : null;

                        const orName = (feedback.offerRequest?.firstName || feedback.offerRequest?.lastName)
                          ? `${feedback.offerRequest?.firstName || ''} ${feedback.offerRequest?.lastName || ''}`.trim()
                          : '';
                        const customerName = (feedback as any).customerName || orName || feedback.name || '';

                        return (
                          <tr key={feedback.id} style={{ cursor: 'default' }}>
                            <td>{sourceLabel}</td>
                            <td>{feedback.created ? dateToDateTimeString(new Date(feedback.created)) : '-'}</td>
                            <td>
                              {customerName ? <>{customerName} {emailLink}</> : (emailLink || '-')}
                            </td>
                            <td>
                              {feedback.offerRequest?.id ? (
                                <a 
                                  href={Routes.OFFER_REQUEST(feedback.offerRequest.id)} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  style={{ textDecoration: 'underline' }}
                                >
                                  {t(ETranslation.COMMON_SEARCH)}
                                </a>
                              ) : '-'}
                            </td>
                            <td>{serviceName || '-'}</td>
                            <td>{mapStars(feedback.serviceSatisfaction) || '-'}</td>
                            <td>
                              {isPublic
                                ? '-'
                                : feedback.serviceProvider
                                  ? feedback.serviceProvider.name
                                  : (feedback.otherFeedback || feedback.serviceProviderOther)
                                    ? (feedback.otherFeedback || feedback.serviceProviderOther)
                                    : (feedback.isServiceProviderNotFound ? t(ETranslation.FEEDBACK_FORM_NOT_FOUND) : '-')}
                            </td>
                            <td className={classes.PriceCell}>
                              {feedback.realizedPrice ? (feedback.realizedPrice.includes('€') || feedback.realizedPrice.toLowerCase().includes('eur') ? feedback.realizedPrice : `${feedback.realizedPrice} €`) : '-'}
                            </td>
                            <td>{feedback.offerCountSatisfaction || '-'}</td>
                            <td>{mapStars(feedback.trustScore) || '-'}</td>
                            <td className={classes.MessageCell} title={feedback.message || undefined}>
                              {feedback.message || '-'}
                            </td>
                            <td>
                              {feedback.requestInfo?.properties && Object.keys(feedback.requestInfo.properties).length > 0 ? (
                                <Button 
                                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                                  onClick={(e) => showRequestInfo(e, feedback)}
                                >
                                  Info
                                </Button>
                              ) : '-'}
                            </td>
                            <td>
                              <Button 
                                style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                                color={EButtonColor.DANGER}
                                onClick={(e) => confirmDelete(e, feedback.id)}
                                icon={faTrash}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {showFloatingBottom && tableRect && (
                  <div 
                    ref={bottomScrollRef}
                    className={classes.FloatingBottomScrollbar}
                    style={{ left: tableRect.left, width: tableRect.width }}
                    onScroll={handleBottomScroll}
                  >
                    <div style={{ width: scrollWidth, height: 1 }} />
                  </div>
                )}
              </>
            )
            : <p>{t(ETranslation.NO_FEEDBACK)}</p>
        }
      </Container>
    </>
  );
}

export default Feedbacks;